import { Component, Input, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

// ─────────────────────────────────────────────────────────────────────────────

export interface DetectedObjectItem {
  name:     string;   // library_object_name
  tag:      string;   // postobon | competencia | precios | nevera
  count:    number;   // detecciones totales en el dataset
  avgScore: number;   // promedio de confidence (0-1)
}

export interface DetectedObjectsConfig {
  title:  string;
  items:  DetectedObjectItem[];
}

// ─────────────────────────────────────────────────────────────────────────────

const TAG_META: Record<string, { label: string; color: string; bg: string }> = {
  postobon:    { label: 'Postobon',    color: '#6366f1', bg: 'rgba(99,102,241,.12)' },
  competencia: { label: 'Competencia', color: '#f43f5e', bg: 'rgba(244,63,94,.12)'  },
  precios:     { label: 'Precios',     color: '#f59e0b', bg: 'rgba(245,158,11,.12)' },
  nevera:      { label: 'Nevera',      color: '#14b8a6', bg: 'rgba(20,184,166,.12)' },
};

const SCORE_COLOR = (s: number) =>
  s >= 0.9 ? '#4ade80' : s >= 0.7 ? '#fb923c' : '#f87171';

@Component({
  selector: 'app-detected-objects-card',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './detected-objects-card.component.html',
  styleUrl:    './detected-objects-card.component.scss',
})
export class DetectedObjectsCardComponent implements OnChanges {
  @Input() config!: DetectedObjectsConfig;
  @Input() loading = false;

  activeTag = 'all';
  tags: { key: string; label: string; color: string }[] = [];
  filtered: (DetectedObjectItem & { meta: typeof TAG_META[string]; scoreColor: string })[] = [];

  ngOnChanges(): void {
    this.build();
  }

  private build(): void {
    if (!this.config?.items?.length) { this.filtered = []; return; }

    // Tags presentes en el dataset
    const tagKeys = [...new Set(this.config.items.map(i => i.tag))].filter(t => TAG_META[t]);
    this.tags = [
      { key: 'all', label: 'Todos', color: '#94a3b8' },
      ...tagKeys.map(k => ({ key: k, label: TAG_META[k].label, color: TAG_META[k].color })),
    ];

    this.applyFilter();
  }

  applyFilter(): void {
    const items = this.activeTag === 'all'
      ? this.config.items
      : this.config.items.filter(i => i.tag === this.activeTag);

    this.filtered = items
      .sort((a, b) => b.count - a.count)
      .map(item => ({
        ...item,
        meta:       TAG_META[item.tag] ?? { label: item.tag, color: '#94a3b8', bg: 'rgba(148,163,184,.12)' },
        scoreColor: SCORE_COLOR(item.avgScore),
      }));
  }

  selectTag(key: string): void {
    this.activeTag = key;
    this.applyFilter();
  }

  scorePct(s: number): number { return Math.round(s * 100); }

  get totalCount(): number {
    return this.config?.items?.reduce((a, i) => a + i.count, 0) ?? 0;
  }

  trackByName(idx: number, item: DetectedObjectItem): string {
    return `${item.tag}-${item.name}`;
  }
}
